import { createSelector } from "reselect";
import moment from "moment-timezone";
import { INTERVAL_TYPE } from "../redux/actions/common/TimeInterval";
import {
  getGraphData,
  getGraphDates,
  getGraphEntries,
  getGraphParams,
  getTableData,
  getTableDates,
} from "../redux/reducersCommon/graphAndTableHelpers";
import {
  getSelectedInterval,
  getSelectedIntervalCode,
} from "../redux/reducersCommon/selectedInterval";
import { getDetailErrorMessages } from "./common/helpers";
import { orgIdSelector } from "./orgIdSelector";

import { getEps } from "../utils/index";

/*
Constants and helpers
*/

export const getDefaultInterval = () => ({
  dateRange: {
    timeEnd: moment().tz(moment.tz.guess(true)),
    timeStart: moment().tz(moment.tz.guess(true)).subtract(24, "hours"),
  },
  intervalType: INTERVAL_TYPE.CUSTOM,
  relativeEnd: "",
  relativeStart: "",
});

const ERROR_KEYS = ["graphError", "overviewError", "tableError"];

/*
Selectors
*/

const logdevsSelector = (state) => state.logdevs;

// Interval selector
export const intervalSelector = createSelector(
  [logdevsSelector, orgIdSelector],
  getSelectedInterval(getDefaultInterval())
);

// Interval code selector
export const intervalCodeSelector = createSelector(
  [intervalSelector],
  getSelectedIntervalCode
);

// Graph raw data selector
export const graphDataSelector = createSelector(
  [intervalCodeSelector, logdevsSelector, orgIdSelector],
  getGraphData
);

// Graph prepared data/entries selector
export const graphEntriesSelector = createSelector(
  [graphDataSelector],
  getGraphEntries({ labelKey1: "logdev", labelKey2: "logType" })
);

// Graph dates selector
export const graphDatesSelector = createSelector(
  [intervalCodeSelector, logdevsSelector, orgIdSelector],
  getGraphDates
);

// Graph params selector
export const graphParamsSelector = createSelector(
  [intervalCodeSelector, logdevsSelector, orgIdSelector],
  getGraphParams
);

// Logdevs Overview selector
const getOverview = (logdevs, orgId) => {
  const { overviewByOrg = {}, overviewDates = {} } = logdevs;

  const { [orgId]: overview = {} } = overviewByOrg;

  const { [orgId]: dates = {} } = overviewDates;

  const { logEventCount: logsTotal = 0 } = overview;

  const eps = getEps({
    logsTotal,
    ...dates,
  });

  return {
    ...overview,
    eps,
  };
};

export const logdevsOverviewSelector = createSelector(
  [logdevsSelector, orgIdSelector],
  getOverview
);

// Table raw data selector
export const tableDataSelector = createSelector(
  [intervalCodeSelector, logdevsSelector, orgIdSelector],
  getTableData
);

// Graph dates selector
export const tableDatesSelector = createSelector(
  [intervalCodeSelector, logdevsSelector, orgIdSelector],
  getTableDates
);

// Error messages selector
export const errorMessagesSelector = createSelector(
  [logdevsSelector],
  getDetailErrorMessages(ERROR_KEYS)
);
